import { FC } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Controller } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'
import { BillingAddress, ShippingAddress } from '@lib/api/cart'
import { FormTextInput } from '@components/ui/Form/FormTextInput'
import { FormDropdown } from '@components/ui/Form/FormDropdown'
import { states } from '@lib/shipping/states'
import { FormCheckbox } from '@components/ui/Form/FormCheckbox'
import { StandardButton } from '@components/ui/StandardButton'
import { Loader } from '@components/icons'
import { getBillingFormInput, getShippingFormInput } from '../helpers'
import { useAddressForm } from './useAddressForm'
import 'react-phone-number-input/style.css'

const countries = [
  {
    label: 'United States of America',
    value: 'US',
    disabled: false,
  },
]

interface ShippingAddressViewProps {
  currentShippingAddress?: ShippingAddress
  currentBillingAddress?: BillingAddress
}
export const ShippingAddressView: FC<ShippingAddressViewProps> = ({
  currentShippingAddress,
  currentBillingAddress,
}) => {
  const { t } = useTranslation()

  const {
    shippingControl,
    shippingErrors,
    billingControl,
    billingErrors,
    isBillingChecked,
    setIsBillingChecked,
    onExit,
    loading,
    onSubmit,
  } = useAddressForm({
    currentShippingAddress: getShippingFormInput(currentShippingAddress),
    currentBillingAddress: getBillingFormInput(currentBillingAddress),
  })

  return (
    <>
      <h2 className="text-blue text-4xl font-archivoBlack mt-2 mb-9 sm:my-9">
        {t('checkout:shipping_address')}
      </h2>
      <form onSubmit={onSubmit} className="flex flex-col space-y-4">
        {Object.values(shippingErrors).length > 0 &&
        Object.values(shippingErrors)[0].message !== undefined ? (
          <p className="text-error">
            <span className="font-bold">{t('checkout:error')}</span>{' '}
            {Object.values(shippingErrors)[0].message}
          </p>
        ) : null}
        <Controller
          control={shippingControl}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <FormTextInput
              id="shipping-email"
              label={`${t('checkout:email_field')}*`}
              error={error}
              {...field}
            />
          )}
        />
        <Controller
          control={shippingControl}
          name="zone"
          render={({ field, fieldState: { error } }) => (
            <FormDropdown
              id="shipping-country"
              items={countries}
              selectedLabel={
                countries.find((coutry) => coutry.value === field.value)?.label
              }
              placeholder={`${t('checkout:zone_field')}*`}
              error={error}
              {...field}
              onSelect={(index: number) => {
                field.onChange({ target: { value: countries[index].value } })
              }}
            />
          )}
        />
        <div className="flex flex-col space-y-4 w-full lg:flex-row lg:space-y-0 lg:space-x-4">
          <Controller
            control={shippingControl}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <FormTextInput
                id="shipping-first-name"
                label={`${t('checkout:first_name_field')}*`}
                error={error}
                {...field}
              />
            )}
          />
          <Controller
            control={shippingControl}
            name="lastName"
            render={({ field, fieldState: { error } }) => (
              <FormTextInput
                id="shipping-last-name"
                label={`${t('checkout:last_name_field')}*`}
                error={error}
                {...field}
              />
            )}
          />
        </div>
        <Controller
          control={shippingControl}
          name="address"
          render={({ field, fieldState: { error } }) => (
            <FormTextInput
              id="shipping-address"
              label={`${t('checkout:address_field')}*`}
              error={error}
              {...field}
            />
          )}
        />
        <Controller
          control={shippingControl}
          name="addressDetails"
          render={({ field, fieldState: { error } }) => (
            <FormTextInput
              id="shipping-address-details"
              label={`${t('checkout:address_details_field')}`}
              error={error}
              {...field}
            />
          )}
        />
        <Controller
          control={shippingControl}
          name="province"
          render={({ field, fieldState: { error } }) => (
            <FormDropdown
              id="shipping-province"
              items={states}
              selectedLabel={
                states.find((state) => state.value === field.value)?.label
              }
              placeholder={`${t('checkout:province_field')}*`}
              error={error}
              {...field}
              onSelect={(index: number) => {
                field.onChange({ target: { value: states[index].value } })
              }}
            />
          )}
        />
        <div className="flex flex-col space-y-4 w-full lg:flex-row lg:space-y-0 lg:space-x-4">
          <Controller
            control={shippingControl}
            name="zipcode"
            render={({ field, fieldState: { error } }) => (
              <FormTextInput
                id="shipping-zipcode"
                label={`${t('checkout:zipcode_field')}*`}
                error={error}
                {...field}
              />
            )}
          />
          <Controller
            control={shippingControl}
            name="city"
            render={({ field, fieldState: { error } }) => (
              <FormTextInput
                id="shipping-city"
                label={`${t('checkout:city_field')}*`}
                error={error}
                {...field}
              />
            )}
          />
        </div>
        <Controller
          control={shippingControl}
          name="phone"
          render={({ field, fieldState: { error } }) => (
            <PhoneInput
              inputComponent={FormTextInput}
              id="shipping-phone"
              label={`${t('checkout:phone_field')}*`}
              error={error}
              {...field}
              onChange={(value) =>
                field.onChange({ target: { value: value ?? '' } })
              }
            />
          )}
        />
        <div className="mt-10">
          <h2 className="text-blue text-4xl font-archivoBlack mb-4">
            {t('checkout:billing_address')}
          </h2>
          <div className="ml-2">
            <FormCheckbox
              label={t('checkout:same_as_shipping')}
              checked={isBillingChecked}
              onChange={() => setIsBillingChecked(!isBillingChecked)}
            />
          </div>
        </div>
        {Object.values(billingErrors).length > 0 &&
        Object.values(billingErrors)[0].message !== undefined ? (
          <p className="text-error">
            <span className="font-bold">{t('checkout:error')}</span>{' '}
            {Object.values(billingErrors)[0].message}
          </p>
        ) : null}
        {isBillingChecked ? null : (
          <div className="flex flex-col space-y-4">
            <Controller
              control={billingControl}
              name="zone"
              render={({ field, fieldState: { error } }) => (
                <FormDropdown
                  id="billing-country"
                  items={countries}
                  selectedLabel={
                    countries.find((coutry) => coutry.value === field.value)
                      ?.label
                  }
                  placeholder={`${t('checkout:zone_field')}*`}
                  error={error}
                  {...field}
                  onSelect={(index: number) => {
                    field.onChange({
                      target: { value: countries[index].value },
                    })
                  }}
                />
              )}
            />
            <div className="flex flex-col space-y-4 w-full lg:flex-row lg:space-y-0 lg:space-x-4">
              <Controller
                control={billingControl}
                name="firstName"
                render={({ field, fieldState: { error } }) => (
                  <FormTextInput
                    id="billing-first-name"
                    label={`${t('checkout:first_name_field')}*`}
                    error={error}
                    {...field}
                  />
                )}
              />
              <Controller
                control={billingControl}
                name="lastName"
                render={({ field, fieldState: { error } }) => (
                  <FormTextInput
                    id="billing-last-name"
                    label={`${t('checkout:last_name_field')}*`}
                    error={error}
                    {...field}
                  />
                )}
              />
            </div>
            <Controller
              control={billingControl}
              name="address"
              render={({ field, fieldState: { error } }) => (
                <FormTextInput
                  id="billing-address"
                  label={`${t('checkout:address_field')}*`}
                  error={error}
                  {...field}
                />
              )}
            />
            <Controller
              control={billingControl}
              name="addressDetails"
              render={({ field, fieldState: { error } }) => (
                <FormTextInput
                  id="billing-address-details"
                  label={`${t('checkout:address_details_field')}`}
                  error={error}
                  {...field}
                />
              )}
            />
            <Controller
              control={billingControl}
              name="province"
              render={({ field, fieldState: { error } }) => (
                <FormDropdown
                  id="billing-province"
                  items={states}
                  selectedLabel={
                    states.find((state) => state.value === field.value)?.label
                  }
                  placeholder={`${t('checkout:province_field')}*`}
                  error={error}
                  {...field}
                  onSelect={(index: number) => {
                    field.onChange({ target: { value: states[index].value } })
                  }}
                />
              )}
            />
            <div className="flex flex-col space-y-4 w-full lg:flex-row lg:space-y-0 lg:space-x-4">
              <Controller
                control={billingControl}
                name="zipcode"
                render={({ field, fieldState: { error } }) => (
                  <FormTextInput
                    id="billing-zipcode"
                    label={`${t('checkout:zipcode_field')}*`}
                    error={error}
                    {...field}
                  />
                )}
              />
              <Controller
                control={billingControl}
                name="city"
                render={({ field, fieldState: { error } }) => (
                  <FormTextInput
                    id="billing-city"
                    label={`${t('checkout:city_field')}*`}
                    error={error}
                    {...field}
                  />
                )}
              />
            </div>
            <Controller
              control={billingControl}
              name="phone"
              render={({ field, fieldState: { error } }) => (
                <PhoneInput
                  inputComponent={FormTextInput}
                  id="billing-phone"
                  label={`${t('checkout:phone_field')}*`}
                  error={error}
                  {...field}
                  onChange={(value) =>
                    field.onChange({ target: { value: value ?? '' } })
                  }
                />
              )}
            />
          </div>
        )}
        <div className="flex flex-col lg:flex-row-reverse">
          <div className="flex flex-col items-end mx-auto lg:m-1 w-[283px]">
            <StandardButton
              type="submit"
              label={
                loading ? (
                  <Loader className="m-auto" />
                ) : (
                  t('checkout:shipping_method')
                )
              }
              disabled={loading}
              onClick={() => {}}
            />
          </div>
          <button
            type="button"
            className="mx-auto lg:ml-0 lg:mr-auto mb-auto h-16 whitespace-nowrap"
            onClick={onExit}
          >
            {`< ${t('checkout:back_button')}`}
          </button>
        </div>
      </form>
    </>
  )
}
